import { ApolloClient, InMemoryCache, createHttpLink, NormalizedCacheObject, ApolloLink } from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { onError } from '@apollo/client/link/error';

import { env } from '@/environment';
import { resolveApiDomainName } from '@/helpers/apiContextDomain';
import { logWarn } from '@/helpers/logger/logUtils';

if (env.NODE_ENV === 'development') {
  loadDevMessages();
  loadErrorMessages();
}

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => logWarn(`[GQL]: "${path}" ${message} (${locations})`));
  }
  if (networkError) {
    logWarn(`[GQL Network ERROR]: ${networkError}`);
  }
});

function getAuthLink(type: 'bearer' | 'basic', token: string): ApolloLink {
  return new ApolloLink((operation, forward) => {
    // Add the Authorization header only if the token is provided
    if (token) {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          Authorization: `${type} ${token}`,
        },
      }));
    }
    return forward(operation);
  });
}

const createApolloClientBase = (
  apiDomainName: string,
  token: string,
  authType: 'bearer' | 'basic',
): ApolloClient<NormalizedCacheObject> => {
  const resolvedApiDomainName = resolveApiDomainName({ apiDomainName });
  const httpLink = createHttpLink({ uri: `${resolvedApiDomainName}/graphql` });
  const authLink = getAuthLink(authType, token);
  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: authLink.concat(errorLink).concat(httpLink),
    cache: new InMemoryCache(),
  });
};

export const createApolloClient = (apiDomainName: string, accessToken: string): ApolloClient<NormalizedCacheObject> =>
  createApolloClientBase(apiDomainName, accessToken, 'bearer');

export const createBasicApolloClient = (
  apiDomainName: string,
  basicToken: string,
): ApolloClient<NormalizedCacheObject> => createApolloClientBase(apiDomainName, basicToken, 'basic');
